import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { API_URL } from '../config';

const TeamManager = () => {
  const [team, setTeam] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    image: '',
    bio: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchTeam();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  const fetchTeam = () => {
    // Public endpoint for listing (no auth needed for read-only if desired, but we use public endpoint here)
    fetch(`${API_URL}/api/public/team`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch team');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          setTeam(data);
        } else {
          console.error('Invalid team data:', data);
          setTeam([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setTeam([]);
        setLoading(false);
      });
  };

  const handleDelete = async (id: number) => {
    if (!id) {
      alert('Error: Invalid team member ID');
      return;
    }
    if (confirm('Are you sure you want to delete this team member?')) {
      try {
        const res = await fetch(`${API_URL}/api/public/admin/team/${id}`, { 
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        const data = await res.json();
        
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to delete');
        }
        fetchTeam();
      } catch (error: any) {
        console.error('Delete failed:', error);
        alert(`Failed to delete team member: ${error.message}`);
      }
    }
  };

  const handleOpenModal = (member?: any) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        role: member.role,
        image: member.image || '',
        bio: member.bio || ''
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        role: '',
        image: '',
        bio: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('File is too large. Please choose an image under 2MB.');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image: reader.result as string });
      setUploading(false);
    };
    reader.onerror = () => {
      alert('Failed to read file');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingMember ? `${API_URL}/api/public/admin/team/${editingMember.id}` : `${API_URL}/api/public/admin/team`;
    const method = editingMember ? 'PUT' : 'POST';
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to save');
      }
      
      setIsModalOpen(false);
      fetchTeam();
    } catch (error: any) {
      console.error('Save failed:', error);
      alert(`Failed to save team member: ${error.message}`);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Team Management</h1>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-orange-600 text-white px-4 py-2 rounded flex items-center hover:bg-orange-700 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Add Member
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4">Name</th>
                <th className="p-4">Role</th>
                <th className="p-4">Bio</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500">Loading team...</td></tr>
              ) : team.length === 0 ? (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500">No team members found.</td></tr>
              ) : (
                team.map((member) => (
                  <tr key={member.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-900 flex items-center">
                      {member.image ? (
                        <img src={member.image} alt={member.name} className="w-8 h-8 rounded-full mr-3 object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full mr-3 bg-slate-200 flex items-center justify-center text-slate-500">
                          <ImageIcon size={16} />
                        </div>
                      )}
                      {member.name}
                    </td>
                    <td className="p-4 text-slate-600">{member.role}</td>
                    <td className="p-4 text-slate-500 text-sm max-w-xs truncate">{member.bio}</td>
                    <td className="p-4 text-right space-x-2">
                      <button 
                        onClick={() => handleOpenModal(member)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => handleDelete(member.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900">
                {editingMember ? 'Edit Member' : 'Add New Member'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <input 
                  type="text" 
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Profile Image</label>
                <div className="flex items-center space-x-4">
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="w-16 h-16 rounded-full object-cover border border-slate-200" />
                  )}
                  <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-lg transition-colors flex items-center">
                    <Upload size={18} className="mr-2" />
                    {uploading ? 'Processing...' : 'Upload Photo'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-1">Max size: 2MB. Formats: JPG, PNG, WebP</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
                <textarea 
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 h-24"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t border-slate-100">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={uploading}
                  className={`px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploading ? 'Uploading...' : 'Save Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManager;
